/**
 * The one place this SPA talks to the worker.
 *
 * SAME ORIGIN. `0722.esimwithnumber.com` serves the Pages build and routes
 * `/api/*` to the worker on the same zone, so there is no base URL, no CORS
 * preflight and no `credentials` option to get wrong. In development Vite's
 * proxy does the same thing, which is why the dev server has one -- otherwise
 * we would be building a CORS layer that production does not want.
 *
 * THE BEARER IS NOT THE PERIMETER. Cloudflare Access is, in front of the whole
 * hostname, and the worker binds every session to the identity Access asserted.
 * A token lifted out of this tab is useless to anybody who cannot also pass
 * Access as the same person.
 *
 * WHY sessionStorage AND NOT localStorage. A session lasts a month on the
 * server and is bound to one person; `localStorage` would keep it alive across
 * a browser restart on a machine that may be shared, and would share it between
 * tabs opened for different reasons. The longer the server-side session, the
 * more that would cost -- a month-old bearer surviving every restart on a
 * shared machine is a different proposition from a twelve-hour one.
 * `sessionStorage` is per-tab and dies with the tab, which is the behaviour an
 * operator tool should have. It is also wrapped:
 * a browser with storage blocked must still be able to sign in and work, it
 * just will not survive a reload.
 */

const TOKEN_KEY = "ewn.admin.token";

/** In-memory copy, so a blocked or full storage does not cost the session. */
let memoryToken: string | null = null;

export function setToken(token: string): void {
  memoryToken = token;
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* Private mode, or storage disabled. The in-memory copy still works. */
  }
}

export function getToken(): string | null {
  if (memoryToken !== null) return memoryToken;
  try {
    memoryToken = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    memoryToken = null;
  }
  return memoryToken;
}

export function clearToken(): void {
  memoryToken = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* Nothing to clear if it was never stored. */
  }
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Codes that mean THE CREDENTIAL was wrong, not that the session is dead.
 *
 * Both come back as 401. A wrong password on the sign-in screen, or a wrong
 * current password on the change-password form, must leave the operator where
 * they are with the error in front of them -- dropping them to the sign-in
 * screen would throw away the form they had just filled in, and on the change
 * form it would be a sign-out for a typo.
 *
 * The worker sends `bad_credentials` for exactly this and `no_session` /
 * `no_access` for a dead one, which is why the two can be told apart at all.
 */
const CREDENTIAL_CODES = new Set(["bad_credentials", "slow_down"]);

/** Callers that want to know the session died, so the app can show sign-in. */
type SessionEndedHandler = () => void;
let onSessionEnded: SessionEndedHandler = () => {};
export function setSessionEndedHandler(handler: SessionEndedHandler): void {
  onSessionEnded = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.ok) {
    // 200 with an empty body is not a shape this API produces, but a proxy
    // between here and the worker can still serve one. `null` is a better
    // answer for a caller to handle than a parse exception.
    return (await res.json().catch(() => null)) as T;
  }

  const payload = (await res.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  const code = payload?.error?.code ?? "network";
  const message = payload?.error?.message ?? "The request did not go through";

  // A DEAD SESSION IS NOT AN ERROR THE SCREEN SHOULD PRINT. It is a state
  // change: the app has to go back to sign-in. Handled through a callback
  // rather than a `location.assign`, because a hard navigation here races the
  // router's own transition and reloads the page mid-move -- the sibling panel
  // carries a comment about exactly that, earned.
  if (res.status === 401 && !CREDENTIAL_CODES.has(code)) {
    clearToken();
    onSessionEnded();
  }

  throw new ApiError(code, message, res.status);
}

export const apiGet = <T>(path: string) => request<T>("GET", path);
export const apiPost = <T>(path: string, body?: unknown) => request<T>("POST", path, body);
export const apiPut = <T>(path: string, body?: unknown) => request<T>("PUT", path, body);
export const apiDelete = <T>(path: string) => request<T>("DELETE", path);

/**
 * A multipart upload, which is the one request that does not send JSON.
 *
 * The `Content-Type` is deliberately NOT set: the runtime writes it, with the
 * boundary. A hand-written one produces a body nothing can parse and an error
 * that reads like the file was rejected.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`/api${path}`, { method: "POST", headers, body: form });
  if (res.ok) return (await res.json().catch(() => null)) as T;

  const payload = (await res.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  const code = payload?.error?.code ?? "network";
  if (res.status === 401 && !CREDENTIAL_CODES.has(code)) {
    clearToken();
    onSessionEnded();
  }
  throw new ApiError(code, payload?.error?.message ?? "The upload did not go through", res.status);
}
